import { Instagram, Facebook, Youtube, Phone, Mail, MapPin, ArrowUpRight, Download } from "lucide-react";
import { CONTACT, SOCIALS, SERVICE_AREAS, IMAGES } from "@/data/content";
import { useInstall } from "@/context/InstallContext";
import { toast } from "@/components/ui/sonner";

const ICONS = { instagram: Instagram, facebook: Facebook, youtube: Youtube };

// Trademark brand colours for each social platform's icon box.
const BRAND = {
  instagram: "bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5] border-transparent text-white",
  facebook: "bg-[#1877F2] border-transparent text-white",
  tiktok: "bg-black border-white/20 text-white",
  youtube: "bg-[#FF0000] border-transparent text-white",
};

const TikTok = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" {...props}>
    <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-2.59-2.59c.27 0 .53.04.77.12v-3.2a5.8 5.8 0 0 0-.77-.05A5.8 5.8 0 1 0 15.54 15V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3a4.28 4.28 0 0 1-3.24-1.48Z" />
  </svg>
);

const NAV = [
  ["Residential", "#residential"], ["Commercial", "#commercial"], ["Logistics", "#logistics"],
  ["Why Us", "#why"], ["Service Areas", "#areas"], ["Client Policy", "#policy"], ["FAQ", "#faq"],
];

export const Footer = () => {
  const go = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  const { canInstall, promptInstall, isIOS, installed } = useInstall();
  const handleInstall = async () => {
    if (canInstall) { await promptInstall(); return; }
    if (isIOS) { toast("On iPhone: tap the Share button, then \"Add to Home Screen\""); return; }
    toast("Open perfecttouches.net on your phone, then choose \"Add to Home Screen\"");
  };

  return (
    <footer className="relative bg-[#050505] border-t border-white/10">
      {/* CTA strip */}
      <div className="border-b border-white/10">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-12 flex items-center justify-center md:justify-start">
          <button data-testid="footer-cta-quote" onClick={() => go("#quote")}
            className="group inline-flex items-center gap-2 bg-gold text-black font-semibold px-8 py-4 hover:bg-[#c5a059] transition-colors duration-300">
            Get a Free Quote
            <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-5 md:px-10 pt-16 pb-10">
        <div className="grid gap-12 lg:grid-cols-12">
          {/* Brand + logo */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-5">
              <div className="bg-white rounded-xl p-3 shrink-0">
                <img
                  src={IMAGES.logo}
                  alt="Perfect Touch Detailed Cleaning Services & Organization logo"
                  className="h-24 md:h-28 w-auto"
                />
              </div>
              <div className="leading-tight">
                <span className="block font-display font-bold text-2xl md:text-3xl gold-gradient">Perfect Touch</span>
                <span className="block font-display text-base md:text-lg text-gold mt-1">Detailed Cleaning &amp; Logistics</span>
              </div>
            </div>
            <p className="mt-6 max-w-md text-sm text-zinc-400 leading-relaxed">
              Professional residential cleaning, selected commercial cleaning, and on-demand logistics
              serving Queens, Brooklyn, Manhattan, selected Long Island &amp; Nassau County areas.
            </p>
            <div className="mt-6 space-y-3">
              <a href={CONTACT.phoneHref} data-testid="footer-phone" className="flex items-center gap-3 text-zinc-300 hover:text-gold transition-colors">
                <Phone size={16} className="text-gold" /> {CONTACT.phone}
              </a>
              <a href={CONTACT.emailHref} data-testid="footer-email" className="flex items-center gap-3 text-zinc-300 hover:text-gold transition-colors">
                <Mail size={16} className="text-gold" /> {CONTACT.email}
              </a>
            </div>
          </div>

          {/* Explore */}
          <div className="lg:col-span-3">
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Explore</span>
            <ul className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {NAV.map(([label, href]) => (
                <li key={href}>
                  <button onClick={() => go(href)} className="text-zinc-400 hover:text-gold transition-colors"
                    data-testid={`footer-nav-${label.toLowerCase().replace(/\s/g, "-")}`}>
                    {label}
                  </button>
                </li>
              ))}
            </ul>

            <span className="mt-8 block text-xs uppercase tracking-[0.2em] text-gold font-semibold">Service Areas</span>
            <p className="mt-4 text-sm text-zinc-400 leading-relaxed">{SERVICE_AREAS.join(" · ")}</p>
          </div>

          {/* Social */}
          <div className="lg:col-span-4">
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Follow Perfect Touch</span>
            <ul className="mt-5 space-y-3">
              {SOCIALS.map((s) => {
                const Icon = s.icon === "tiktok" ? TikTok : ICONS[s.icon];
                const inner = (
                  <span className="flex items-center gap-3 text-zinc-300 group-hover:text-white transition-colors">
                    <span className={`w-9 h-9 flex items-center justify-center rounded-md border transition-transform duration-300 group-hover:scale-110 ${BRAND[s.icon] || "border-white/10 text-zinc-400"}`}>
                      <Icon />
                    </span>
                    <span className="text-sm">{s.handle}</span>
                  </span>
                );
                return (
                  <li key={s.name}>
                    {s.url ? (
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="group inline-flex" data-testid={`social-${s.icon}`}>{inner}</a>
                    ) : (
                      <span className="group inline-flex" data-testid={`social-${s.icon}`} title={`${s.name} — link coming soon`}>{inner}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-zinc-600">
            © {new Date().getFullYear()} Perfect Touch Detailed Cleaning &amp; Logistics Services. All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            {!installed && (
              <button
                data-testid="footer-install-app"
                onClick={handleInstall}
                className="inline-flex items-center gap-2 text-xs text-gold hover:text-white border border-gold/40 hover:border-gold px-4 py-2 transition-colors duration-300"
              >
                <Download size={14} /> Install App
              </button>
            )}
            <p className="text-xs text-zinc-600">Detailed workmanship. Serving NYC.</p>
          </div>
        </div>
      </div>
    </footer>
  );
};
